/**
 * Controles virtuais transparentes na tela para versão Mobile.
 *
 * Oferece D-Pad direcional, botões de ação (A, B, X, Y) e atalhos rápidos
 * de Menu, Tela Cheia e Auto-batalha com estética transparente / glassmorphic.
 *
 * Caso um joystick físico seja ativado no mobile, os botões somem automaticamente.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export interface VirtualTouchControlsProps {
  readonly onDirection?: (dx: number, dy: number) => void;
  readonly onConfirm?: () => void;
  readonly onCancel?: () => void;
  readonly onCycleTarget?: () => void;
  readonly onToggleThreat?: () => void;
  readonly onMenu?: () => void;
  readonly onToggleFullscreen?: () => void;
  readonly isFullscreen?: boolean;
  readonly autoBattle?: boolean;
  readonly onToggleAutoBattle?: () => void;
  readonly visible?: boolean;
  /** Qual botão confirma no padrão do jogador ('inferior' = A embaixo; 'direita' = A na direita) */
  readonly confirmButton?: 'inferior' | 'direita';
}

function haptic(ms = 10) {
  try {
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      navigator.vibrate(ms);
    }
  } catch {
    // Ignora se não houver permissão
  }
}

export function VirtualTouchControls({
  onDirection,
  onConfirm,
  onCancel,
  onCycleTarget,
  onToggleThreat,
  onMenu,
  onToggleFullscreen,
  isFullscreen,
  autoBattle,
  onToggleAutoBattle,
  visible = true,
  confirmButton = 'inferior',
}: VirtualTouchControlsProps): JSX.Element | null {
  // Se não estiver visível (ex: joystick ativado), não renderiza nada na tela
  if (!visible) return null;

  const repeatTimerRef = useRef<number | null>(null);
  const activeDirRef = useRef<{ dx: number; dy: number } | null>(null);
  const [pressedDir, setPressedDir] = useState<string | null>(null);

  const stopDirection = useCallback(() => {
    if (repeatTimerRef.current !== null) {
      window.clearTimeout(repeatTimerRef.current);
      repeatTimerRef.current = null;
    }
    activeDirRef.current = null;
    setPressedDir(null);
  }, []);

  const startDirection = useCallback(
    (dx: number, dy: number, dirKey: string) => {
      stopDirection();
      haptic(12);
      activeDirRef.current = { dx, dy };
      setPressedDir(dirKey);
      onDirection?.(dx, dy);

      // Repetição com atraso inicial de 260ms e intervalo de 90ms
      const repeat = () => {
        if (!activeDirRef.current) return;
        onDirection?.(activeDirRef.current.dx, activeDirRef.current.dy);
        repeatTimerRef.current = window.setTimeout(repeat, 90);
      };
      repeatTimerRef.current = window.setTimeout(repeat, 260);
    },
    [onDirection, stopDirection],
  );

  useEffect(() => {
    return () => {
      if (repeatTimerRef.current !== null) {
        window.clearTimeout(repeatTimerRef.current);
      }
    };
  }, []);

  // Mapeamento visual e funcional dos botões de acordo com o confirmButton do jogador
  const isBottomConfirm = confirmButton === 'inferior';

  return (
    <div
      className="virtual-touch-overlay"
      aria-label="Controles virtuais na tela"
      data-testid="virtual-touch-controls"
    >
      {/* Barra superior de atalhos discretos (Menu, Tela Cheia, Auto) */}
      <div className="vtc-top-bar">
        {onMenu && (
          <button
            type="button"
            className="vtc-top-btn"
            title="Abrir Ordens de Campo / Menu (Start)"
            onPointerDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              haptic(15);
              onMenu();
            }}
          >
            <span className="vtc-top-icon">☰</span>
            <span className="vtc-top-label">Menu</span>
          </button>
        )}

        {onToggleFullscreen && (
          <button
            type="button"
            className={`vtc-top-btn ${isFullscreen ? 'active-glow' : ''}`}
            title="Alternar Tela Cheia Persistente"
            onPointerDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              haptic(15);
              onToggleFullscreen();
            }}
          >
            <span className="vtc-top-icon">{isFullscreen ? '⛶' : '⛶'}</span>
            <span className="vtc-top-label">{isFullscreen ? 'Cheia' : 'Janela'}</span>
          </button>
        )}

        {onToggleAutoBattle && (
          <button
            type="button"
            className={`vtc-top-btn ${autoBattle ? 'active-gold' : ''}`}
            title="Auto-batalha automática"
            onPointerDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              haptic(15);
              onToggleAutoBattle();
            }}
          >
            <span className="vtc-top-icon">⚡</span>
            <span className="vtc-top-label">Auto</span>
          </button>
        )}
      </div>

      {/* D-Pad direcional no canto inferior esquerdo */}
      <div className="vtc-dpad-cluster" role="group" aria-label="Direcionais">
        <div className="vtc-dpad-base">
          {/* Cima */}
          <button
            type="button"
            className={`vtc-dpad-btn up ${pressedDir === 'up' ? 'pressed' : ''}`}
            aria-label="Cima"
            onPointerDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              startDirection(0, -1, 'up');
            }}
            onPointerUp={(e) => {
              e.preventDefault();
              stopDirection();
            }}
            onPointerCancel={(e) => {
              e.preventDefault();
              stopDirection();
            }}
          >
            ▲
          </button>

          {/* Esquerda */}
          <button
            type="button"
            className={`vtc-dpad-btn left ${pressedDir === 'left' ? 'pressed' : ''}`}
            aria-label="Esquerda"
            onPointerDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              startDirection(-1, 0, 'left');
            }}
            onPointerUp={(e) => {
              e.preventDefault();
              stopDirection();
            }}
            onPointerCancel={(e) => {
              e.preventDefault();
              stopDirection();
            }}
          >
            ◀
          </button>

          {/* Núcleo central visual */}
          <div className="vtc-dpad-center" aria-hidden="true" />

          {/* Direita */}
          <button
            type="button"
            className={`vtc-dpad-btn right ${pressedDir === 'right' ? 'pressed' : ''}`}
            aria-label="Direita"
            onPointerDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              startDirection(1, 0, 'right');
            }}
            onPointerUp={(e) => {
              e.preventDefault();
              stopDirection();
            }}
            onPointerCancel={(e) => {
              e.preventDefault();
              stopDirection();
            }}
          >
            ▶
          </button>

          {/* Baixo */}
          <button
            type="button"
            className={`vtc-dpad-btn down ${pressedDir === 'down' ? 'pressed' : ''}`}
            aria-label="Baixo"
            onPointerDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              startDirection(0, 1, 'down');
            }}
            onPointerUp={(e) => {
              e.preventDefault();
              stopDirection();
            }}
            onPointerCancel={(e) => {
              e.preventDefault();
              stopDirection();
            }}
          >
            ▼
          </button>
        </div>
      </div>

      {/* Cluster de Ação (A, B, X, Y) no canto inferior direito */}
      <div className="vtc-action-cluster" role="group" aria-label="Botões de Ação">
        {/* Y (Topo) - Ameaça / Alcance inimigo */}
        <button
          type="button"
          className="vtc-action-btn btn-y"
          title="Alcance Inimigo (T)"
          onPointerDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            haptic(10);
            onToggleThreat?.();
          }}
        >
          <span className="vtc-btn-letter">Y</span>
          <span className="vtc-btn-sub">Ameaça</span>
        </button>

        {/* X (Esquerda) - Alvo / Ciclar unidades */}
        <button
          type="button"
          className="vtc-action-btn btn-x"
          title="Ciclar Alvos / Unidades (Tab / X)"
          onPointerDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            haptic(10);
            onCycleTarget?.();
          }}
        >
          <span className="vtc-btn-letter">X</span>
          <span className="vtc-btn-sub">Alvo</span>
        </button>

        {/* B (Direita) - Cancelar / Voltar */}
        <button
          type="button"
          className={`vtc-action-btn btn-b ${!isBottomConfirm ? 'action-confirm' : 'action-cancel'}`}
          title={!isBottomConfirm ? 'Confirmar' : 'Cancelar / Voltar'}
          onPointerDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            haptic(12);
            if (!isBottomConfirm) onConfirm?.();
            else onCancel?.();
          }}
        >
          <span className="vtc-btn-letter">B</span>
          <span className="vtc-btn-sub">{!isBottomConfirm ? 'Ação' : 'Voltar'}</span>
        </button>

        {/* A (Inferior) - Confirmar / Ação */}
        <button
          type="button"
          className={`vtc-action-btn btn-a ${isBottomConfirm ? 'action-confirm' : 'action-cancel'}`}
          title={isBottomConfirm ? 'Confirmar / Interagir' : 'Cancelar / Voltar'}
          onPointerDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            haptic(15);
            if (isBottomConfirm) onConfirm?.();
            else onCancel?.();
          }}
        >
          <span className="vtc-btn-letter">A</span>
          <span className="vtc-btn-sub">{isBottomConfirm ? 'Ação' : 'Voltar'}</span>
        </button>
      </div>
    </div>
  );
}
